import { useState, useCallback, useRef, useEffect } from 'react';
import { skippedMessage } from '../services/publishResult';
import { mergeSolvedShifts } from '../services/rosterScope';
import { apiFetch } from '../services/apiClient';
import { toast } from '../components/Toast';

export const useRoster = () => {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [solving, setSolving] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [error, setError] = useState(null);
  // Things worth telling the planner that are NOT faults. Stopping a solve is a
  // deliberate act, so routing its explanation through `error` painted a red
  // failure toast over something the user chose to do.
  const [notice, setNotice] = useState(null);

  // The live poll's interval handle, and the job it belongs to.
  //
  // pollSchedule used to RETURN the interval and startSolving threw it away, so
  // nothing could ever cancel a poll. Pressing Stop cleared the spinner and left
  // the interval running; starting a second solve then produced TWO intervals
  // merging two different rosters into one schedule a second apart. Each roster
  // was individually legal, so the result looked plausible and put people on six
  // consecutive days.
  //
  // Refs, not state: the interval callback closes over its creation render, so a
  // state value read inside it would be permanently stale.
  const pollRef = useRef(null);
  const activeJobRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    activeJobRef.current = null;
  }, []);

  // A poll must never outlive the component that started it.
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const toLocalISO = (date) => {
    if (!date) return '';
    const y = date.getFullYear(), m = String(date.getMonth() + 1).padStart(2, '0'), d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const fetchWeekRange = useCallback(async (startDate, endDate, weekdaysOnly = false) => {
    setLoading(true); setError(null);
    try {
      const s = toLocalISO(startDate);
      const e = toLocalISO(endDate);
      const response = await apiFetch(`/shifts-range?start=${s}&end=${e}${weekdaysOnly ? '&weekdays_only=true' : ''}`);
      // Check the status BEFORE parsing. Without this, a 500 carrying a JSON
      // error body was merged into the schedule as though it were roster data:
      // every field fell back to its default, so a failed fetch was
      // indistinguishable from "this week is genuinely empty" -- and it wiped
      // the week the planner was looking at.
      if (!response.ok) {
        throw new Error(`Could not load ${s} to ${e} (HTTP ${response.status}).`);
      }
      const data = await response.json();

      setSchedule(prev => {
        const base = data || {};
        // Explicitly set the window in the global state so it's ready for solving
        const updated = {
          ...base,
          solveWindowStart: s,
          solveWindowEnd: e
        };

        if (!prev) return updated;
        
        const existingShifts = prev.shifts || [];
        const newShifts = data.shifts || [];
        const newIds = new Set(newShifts.map(ns => ns.id));

        // Preserve any free-text assignment label already in state: a re-fetched
        // shift may arrive without it (e.g. if the running backend predates label
        // persistence), so carry it forward by id rather than let the fresh copy
        // silently drop it. Backend-supplied labels still win when present.
        // PRECEDENCE: the in-memory state is the working draft (solver output
        // and manual edits live ONLY here until Publish), so for any slot this
        // tab already holds, the existing copy's ASSIGNMENT STATE wins outright
        // — including a deliberately empty slot the solver chose to leave open.
        // Mixing directions is worse than either extreme: letting the server's
        // published copy win where it had an employee once produced a chimera
        // of old-published + new-draft rosters in which two individually legal
        // schedules combined into 5-6 consecutive working days for one person.
        // Fetched copies still refresh non-assignment fields (times, flags),
        // fill slots the tab has never seen, and stale in-range ids are pruned.
        // Server truth re-enters wholesale via loadDemoData (the universal
        // refresh) and after Publish.
        const prevById = new Map(existingShifts.map(es => [es.id, es]));
        const newShiftsWithLabels = newShifts.map(ns => {
          const prev = prevById.get(ns.id);
          if (!prev) return ns;
          return {
            ...ns,
            employee: prev.employee ?? null,
            locked: prev.locked ?? false,
            assignmentLabel: prev.assignmentLabel || prev.assignment_label
              || ns.assignmentLabel || ns.assignment_label || undefined,
          };
        });

        // Prune stale in-range ids: an existing shift inside the fetched range
        // whose id the server no longer returns came from a superseded staffing
        // config and must not linger as a phantom slot.
        const inRange = (shift) => {
          const d = (shift.start || '').slice(0, 10);
          return d >= s && d <= e;
        };
        const merged = [
          ...existingShifts.filter(es => !newIds.has(es.id) && !inRange(es)),
          ...newShiftsWithLabels
        ];
        
        return { ...updated, shifts: merged };
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDemoData = useCallback(async (datasetId = 'SMALL', reset = false) => {
    setLoading(true); setError(null);
    try {
      const response = await apiFetch(`/demo-data/${datasetId}${reset ? '?reset=true' : ''}`);
      // Same guard as fetchWeekRange. This one matters more: loadDemoData
      // REPLACES the whole schedule and is the universal refresh called after
      // roughly twenty writes, so parsing an error body here empties the app.
      if (!response.ok) {
        throw new Error(`Could not refresh the roster (HTTP ${response.status}).`);
      }
      const data = await response.json();

      // Ensure all required arrays/sets exist and are properly formatted
      if (data && data.employees) {
        data.employees = data.employees.map(emp => ({
          ...emp,
          skills: Array.from(new Set(emp.skills || [])),
          onCallPreferences: Array.from(new Set(emp.onCallPreferences || emp.on_call_preferences || [])),
          onCallForbiddenDates: Array.from(new Set(emp.onCallForbiddenDates || emp.on_call_forbidden_dates || []))
        }));
      }
      
      // A running poll would write its solve into the schedule we just
      // replaced wholesale, resurrecting the draft this refresh discarded.
      stopPolling();
      setSchedule(data); setCurrentJobId(null); setSolving(false);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  }, [stopPolling]);

  const pollSchedule = useCallback((jobId) => {
    // Only ever one poll alive: a new solve supersedes whatever came before.
    if (pollRef.current) clearInterval(pollRef.current);
    activeJobRef.current = jobId;

    // A solve runs for minutes on the server; one dropped poll is not a reason
    // to abandon it. Previously ANY single failure -- a 502 from the load
    // balancer, a laptop sleeping for a second, one dropped packet -- killed
    // the loop for good, and the catch branch did so without even setting an
    // error: the spinner stopped, nothing appeared, and nothing said why. The
    // solve was still running happily on the server. That is the "it didn't
    // work, I ran it again and it worked" report from UAT.
    let consecutiveFailures = 0;
    const MAX_CONSECUTIVE_FAILURES = 3;

    const giveUp = (message) => {
      stopPolling();
      setSolving(false);
      setError(message);
    };

    const interval = setInterval(async () => {
      // This job has been stopped or superseded -- its results must not reach
      // the roster. Belt and braces alongside stopPolling(): even if an interval
      // somehow survives, it cannot write into a newer solve's draft.
      if (activeJobRef.current !== jobId) { clearInterval(interval); return; }
      try {
        const response = await apiFetch(`/schedules/${jobId}`);

        // 404 is terminal and means something specific: the job is genuinely
        // gone (server restart, or evicted by newer solves). Retrying cannot
        // bring it back, so say so rather than burning the retry budget.
        if (response.status === 404) {
          const body = await response.json().catch(() => null);
          giveUp((body && body.detail)
            || 'That solve is no longer on the server. Please run it again.');
          return;
        }
        if (!response.ok) {
          // Anything else (5xx, a proxy hiccup) may well be transient.
          consecutiveFailures += 1;
          if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
            giveUp(`Lost contact with the solver (HTTP ${response.status}). `
                   + 'The solve may still be running on the server — reload to check.');
          }
          return;
        }
        const data = await response.json();
        consecutiveFailures = 0;   // a good response clears the streak
        
        if (data && data.employees) {
          data.employees = data.employees.map(emp => ({
            ...emp,
            skills: Array.from(new Set(emp.skills || [])),
              onCallPreferences: Array.from(new Set(emp.onCallPreferences || emp.on_call_preferences || [])),
            onCallForbiddenDates: Array.from(new Set(emp.onCallForbiddenDates || emp.on_call_forbidden_dates || []))
          }));
        }

        setSchedule(prev => {
          if (!prev) return data;
          // MERGE solved shifts back into global state. Outside the solve
          // window the browser's own copy wins (see mergeSolvedShifts).
          const merged = mergeSolvedShifts(
            prev.shifts, data.shifts,
            data.solveWindowStart || data.solve_window_start,
            data.solveWindowEnd || data.solve_window_end);
          return { ...prev, ...data, shifts: merged };
        });

        // Stop on ANY terminal status, not just 'NOT_SOLVING'.
        //
        // GET /schedules/{id} used to overwrite the schedule's own status with
        // the job registry's, collapsing OPTIMAL / FEASIBLE / INFEASIBLE / ERROR
        // alike into 'NOT_SOLVING'. That hid failed solves, so the endpoint now
        // reports the real outcome -- and this stop condition, keyed on the
        // flattened value, stopped matching: a finished solve polled forever and
        // the spinner never cleared, with the roster already sitting in state.
        //
        // The contract is: 'SOLVING_ACTIVE' means still running, a missing status
        // means an intermediate callback, anything else is terminal.
        // A late response from a job that has since been stopped or replaced.
        if (activeJobRef.current !== jobId) { clearInterval(interval); return; }

        const status = data.solverStatus || data.solver_status;
        if (status && status !== 'SOLVING_ACTIVE') {
          stopPolling();
          setSolving(false);
          // Surface a failed solve rather than leaving an unchanged grid, which
          // is indistinguishable from "the solver had nothing to do".
          if (status === 'INFEASIBLE' || status === 'ERROR') {
            setError(data.error
              || (status === 'INFEASIBLE'
                    ? 'No roster satisfies every hard rule for this window.'
                    : 'The solve failed. Check the server log for details.'));
          }
          // A lock wins over the rules (Oct 2026); say so when one did.
          const overrides = data.lockOverrides || data.lock_overrides || [];
          if (overrides.length) {
            toast.info(`${overrides.length} lock${overrides.length === 1 ? '' : 's'} kept despite a rule `
              + `(e.g. ${overrides[0].staff} on ${overrides[0].slot}: ${overrides[0].rule}). `
              + 'Check conflicts lists them.');
          }
        }
      } catch (err) {
        // A thrown fetch is the classic transient: DNS blip, connection reset,
        // the machine asleep. Tolerate a few, then give up WITH a reason --
        // this branch used to stop the spinner and say nothing at all.
        consecutiveFailures += 1;
        if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
          giveUp('Lost contact with the solver. The solve may still be running '
                 + 'on the server — reload to check.');
        }
      }
    }, 1000);
    pollRef.current = interval;
    return interval;
  }, [stopPolling]);

  const startSolving = useCallback(async (input) => {
    if (!schedule) { setError('Please load demo data first'); return; }
    setLoading(true);
    
    // A scoped schedule from the console, else the whole schedule. Which rules
    // run is decided by the department's constraints YAML, not the request --
    // the "constraint profile" that used to ride along here was read by nothing.
    const payload = (input && input.shifts) ? input : schedule;

    console.log("[DEBUG] useRoster.startSolving payload window:", payload.solveWindowStart, "TO", payload.solveWindowEnd);

    try {
      const response = await apiFetch('/schedules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const jobId = (await response.text()).replace(/"/g, '');
      setCurrentJobId(jobId); setSolving(true); pollSchedule(jobId);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  }, [schedule, pollSchedule]);

  const stopSolving = useCallback(async () => {
    const jobId = currentJobId;
    if (!jobId) return;

    // Stop the repeating poll FIRST, and locally, so a slow or failed DELETE
    // cannot leave it running and writing into a later solve's draft.
    stopPolling();
    setSolving(false);
    setCurrentJobId(null);

    try {
      await apiFetch(`/schedules/${jobId}`, { method: 'DELETE' });

      // The solver unwinds in about 50ms and then assembles its best roster so
      // far, flagged CANCELLED. Because we just stopped listening we would never
      // see it -- leaving the grid on the last streamed intermediate, with
      // nothing saying the solve was cut short. Collect that final result once.
      for (let attempt = 0; attempt < 10; attempt++) {
        await new Promise(r => setTimeout(r, 150));
        const res = await apiFetch(`/schedules/${jobId}`);
        if (!res.ok) break;
        const data = await res.json();
        const status = data?.solverStatus || data?.solver_status;
        if (!status || status === 'SOLVING_ACTIVE') continue;

        setSchedule(prev => {
          if (!prev) return data;
          const solved = data.shifts || [];
          const solvedIds = new Set(solved.map(s => s.id));
          return { ...prev, ...data,
                   shifts: [...prev.shifts.filter(s => !solvedIds.has(s.id)), ...solved] };
        });
        // Not an error -- an explanation. A stopped roster is complete and legal
        // but less polished than a finished one, and looks identical on screen.
        if (data.error) setNotice(data.error);
        break;
      }
    } catch (err) { setError(err.message); }
  }, [currentJobId, stopPolling]);

  const publishSchedule = useCallback(async (scopedSchedule) => {
    const target = scopedSchedule || schedule;
    if (!target) return;
    try {
      const res = await apiFetch('/schedules/publish', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(target)
      });
      // A 4xx/5xx is a response, not a thrown error: without this check a
      // rejected publish announced success and cleared the unpublished-draft
      // warning for dates that never reached the server.
      if (!res.ok) throw new Error(`Publish failed (HTTP ${res.status}). Nothing was saved — try again.`);
      // Assignments to somebody no longer on the roster (a stale tab) are not
      // recorded; the server names them rather than dropping them in silence.
      const body = await res.json().catch(() => ({}));
      const skipped = skippedMessage(body?.skipped);
      if (skipped) toast.error(skipped);
      else toast.success("Roster published.");
      return true;
    } catch (err) { setError(err.message); return false; }
  }, [schedule]);

  return { schedule, setSchedule, loading, solving, error, notice, setNotice, loadDemoData, fetchWeekRange, toLocalISO, startSolving, stopSolving, publishSchedule };
};
